import { ChevronDown } from 'lucide-react'

interface AccountOption {
  connectionId: string
  displayName: string
  isDefault: boolean
  provider?: string
}

interface AccountPillProps {
  accounts: AccountOption[]
  selectedId: string | undefined
  onTap: () => void
  disabled?: boolean
}

/** Color from a hash of the display name */
function avatarColor(name: string): string {
  const colors = ['#D4A853', '#6B8AED', '#E87D7D', '#7DD4A8', '#C07DDB', '#E8A84C'] as const
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length] ?? '#D4A853'
}

export default function AccountPill({ accounts, selectedId, onTap, disabled }: AccountPillProps) {
  const selected = accounts.find(a => a.connectionId === selectedId) ?? accounts[0]
  if (!selected) return null

  const initial = selected.displayName.charAt(0).toUpperCase()
  const color = avatarColor(selected.displayName)
  const hasMultiple = accounts.length > 1
  const interactive = hasMultiple && !disabled

  // Truncate email to ~24 chars
  const label = selected.displayName.length > 24
    ? selected.displayName.slice(0, 22) + '\u2026'
    : selected.displayName

  return (
    <button
      type="button"
      onClick={interactive ? onTap : undefined}
      disabled={!interactive}
      className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-full bg-charcoal/60 border border-white/10 transition-colors ${
        interactive ? 'hover:border-gold/30 hover:bg-charcoal/80 cursor-pointer' : 'cursor-default'
      }`}
    >
      {/* Avatar dot */}
      <span
        className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-midnight shrink-0"
        style={{ backgroundColor: color }}
      >
        {initial}
      </span>

      <span className="text-[10px] text-pearl/60 leading-none truncate max-w-[160px]">
        {label}
      </span>

      {hasMultiple && (
        <ChevronDown size={10} className="text-pearl/60 shrink-0" />
      )}
    </button>
  )
}
